import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import List from "@mui/material/List";
import ButtonGroup from "@mui/material/ButtonGroup";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Add from "@mui/icons-material/Add";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import UnfoldLess from "@mui/icons-material/UnfoldLess";
import Save from "@mui/icons-material/Save";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useTheme } from "@mui/material/styles";
import { useGet, usePut } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { useAlertDialog } from "../../actprep-ui/hooks/feedback";
import SortableScheduleItem from "./SortableScheduleItem";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import scheduleItemTypeOptions from "../ProgramDayScheduleItemForm/scheduleItemTypeOptions";
import ScheduleItemList from "../../actprep-ui/components/ScheduleItemList";
import Search from "@mui/icons-material/Search";

function createAccordionItems(scheduleItems) {
  return scheduleItems.map((scheduleItem, i) => ({
    id: i + 1,
    title: scheduleItem.label,
    expanded: false,
    edited: false,
    removed: false,
    data: scheduleItem,
  }));
}

export default function ProgramDayScheduleBuilder({ programDay }) {
  const scheduleItemsUrl = useRef(apiUrls.admin.scheduleItems());
  const { data, refetch, isLoading } = useGet(scheduleItemsUrl.current, {
    enabled: !!programDay,
    params: {
      search_params: {
        program_day_id: programDay?.uuid,
      },
    },
    staleTime: 0,
  });

  const {
    getButtonProps: getPreviewButtonProps,
    getDisclosureProps: getPreviewDialogProps,
  } = useDisclosure();

  const [scheduleItems, setScheduleItems] = useState(data?.data || []);
  const { setOpen: setAlertOpen, AlertDialog } = useAlertDialog({
    title: "Unable to save order",
    content:
      "Please save all edited schedule items before saving the schedule order.",
  });
  const [items, setItems] = useState(createAccordionItems(scheduleItems));
  const [scheduleEdited, setScheduleEdited] = useState(false);
  const {
    onClose: onCloseAddItemDialog,
    onOpen: onOpenAddItemDialog,
    getDisclosureProps: getAddItemDialogProps,
  } = useDisclosure();
  const updateScheduleItemOrderMutation = usePut(apiUrls.admin.scheduleItems());
  const { palette: muiPalette } = useTheme();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const setScheduleItemState = useCallback((itemId, state) => {
    setItems((items) =>
      items
        .map((item) => {
          if (item.id === itemId) {
            for (let [key, val] of Object.entries(state)) {
              item[key] = val;
            }
          }
          return item;
        })
        .filter((item) => !item.removed)
    );
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setScheduleEdited(true);
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const allItemsExpanded = () => {
    return items.every((item) => item.expanded);
  };

  const expandAllItems = () => {
    setItems(items.map((item) => ({ ...item, expanded: true })));
  };
  const collapseAllItems = () => {
    setItems(items.map((item) => ({ ...item, expanded: false })));
  };

  const saveOrder = async (force = false) => {
    if (!force && items.some((item) => item.edited)) {
      setAlertOpen(true);
      return;
    }

    const scheduleItemUUIDs = items.map((item) => item.data.uuid);
    const { data } = await updateScheduleItemOrderMutation.mutateAsync({
      reorder: true,
      program_day_uuid: programDay.uuid,
      schedule_item_uuids: scheduleItemUUIDs,
    });
    if (data?.success) {
      showSuccessSnackbar("Schedule saved!");
      setScheduleEdited(false);
      refetch();
    }
  };

  const handleNewItemClick = (newItem) => () => {
    onCloseAddItemDialog();
    setScheduleEdited(true);
    setItems([
      {
        id: 1,
        title: `Untitled '${newItem.title}'`,
        expanded: true,
        data: {
          item_type: newItem.item_type,
          label: "",
          description: "",
          item_metadata: newItem.defaultMetadata,
        },
      },
      ...items.map((item) => ({ ...item, id: item.id + 1 })),
    ]);
  };

  useEffect(() => {
    const scheduleItems = data?.data || [];
    setScheduleItems(scheduleItems);
    setItems(createAccordionItems(scheduleItems));
    setScheduleEdited(false);
  }, [data]);

  return (
    <>
      <Grid container>
        <Grid size={12}>
          <Typography variant="h6">
            {programDay
              ? `${programDay.title} Schedule`
              : "No Schedule Selected"}
          </Typography>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <ButtonGroup
              fullWidth
              variant="text"
              size="small"
              sx={{
                backgroundColor: muiPalette.background.secondary,
                border: `1px solid ${muiPalette.divider}`,
                borderBottom: "none",
                borderBottomLeftRadius: "0",
                borderBottomRightRadius: "0",
              }}
            >
              <Button
                startIcon={<Add />}
                onClick={onOpenAddItemDialog}
                disabled={!programDay}
              >
                Add Item
              </Button>
              {allItemsExpanded() ? (
                <Button
                  startIcon={<UnfoldLess />}
                  onClick={collapseAllItems}
                  disabled={!programDay}
                >
                  Collapse All
                </Button>
              ) : (
                <Button
                  startIcon={<UnfoldMore />}
                  onClick={expandAllItems}
                  disabled={!programDay}
                >
                  Expand All
                </Button>
              )}
              <Button
                startIcon={<Search />}
                {...getPreviewButtonProps()}
                disabled={!programDay}
              >
                Preview
              </Button>
              <Button
                startIcon={<Save />}
                onClick={() => saveOrder()}
                disabled={!(programDay && scheduleEdited)}
              >
                Save
              </Button>
            </ButtonGroup>
            <Box
              sx={{
                padding: "1rem",
                border: `1px solid ${muiPalette.divider}`,
                borderRadius: "1rem",
                borderTopLeftRadius: "0",
                borderTopRightRadius: "0",
                backgroundColor: muiPalette.background.secondary,
                minHeight: "5rem",
              }}
            >
              {isLoading && (
                <Box
                  sx={{ width: "100%" }}
                  display={"flex"}
                  justifyContent={"center"}
                >
                  <CircularProgress />
                </Box>
              )}
              {!isLoading &&
                (items.length ? (
                  <SortableContext
                    items={items}
                    strategy={verticalListSortingStrategy}
                  >
                    {items.map((item, i) => (
                      <SortableScheduleItem
                        key={item.id}
                        order={i}
                        setState={setScheduleItemState}
                        programDay={programDay}
                        {...item}
                      />
                    ))}
                  </SortableContext>
                ) : (
                  <Box
                    sx={{ width: "100%" }}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    No schedule items added
                  </Box>
                ))}
            </Box>
          </DndContext>
        </Grid>
      </Grid>
      <Dialog {...getAddItemDialogProps()}>
        <DialogTitle>Choose a new schedule item</DialogTitle>
        <DialogContent>
          <List>
            {Object.values(scheduleItemTypeOptions).map((newItemOption) => (
              <ListItemButton
                key={newItemOption.title}
                onClick={handleNewItemClick(newItemOption)}
              >
                <ListItemText
                  primary={newItemOption.title}
                  secondary={newItemOption.description}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
      {programDay && (
        <Dialog {...getPreviewDialogProps()}>
          <DialogTitle>{programDay.title} Schedule Preview </DialogTitle>
          <DialogContent>
            <ScheduleItemList
              scheduleItems={items.map((item) => item.data)}
              isPreview={true}
            />
          </DialogContent>
        </Dialog>
      )}
      <AlertDialog />
    </>
  );
}
